import React from 'react';
import { Action as ReduxAction } from 'redux';

import { AskDataMode } from '~src/settings/types/AskDataMode';

import { Messages } from '../../../../codegen/Localize';
import { CommonStyles } from '../../../../common/react/CommonStyles';
import { imgPath } from '../../../../common/utilities/ImgUrl';
import { IDatasourceActions } from '../../../../datasource/ServerApi/types/IDatasourceActions';
import { selectEditingFlowsEnabled } from '../../../../flow/selectors/FlowSelectors';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { ExplorableItemType, SpaceInfoItemTypeValue } from '../../../../ts/models/ContentType';
import { ISpaceTypeInfo } from '../../../../ts/models/resourceInfoParts';
import { ExploreContentApi_ContentType } from '../../../../ts/ServerApi/types/IContent';
import { IProjectActions } from '../../../../ts/ServerApi/types/IProjectActions';
import { SiteRoles } from '../../../../ts/ServerApi/types/ISiteRole';
import { SiteRolesUtil } from '../../../../userManagement/services/resources/SiteRolesUtil';
import { createNewFlow, createNewLens, createNewProject, createNewPublishedConnection, createNewPublishedDataSource, createNewSchedule, createNewSite, createNewWorkbook, createNoNameCollectionAction } from '../../../actions/spaces/ContentExplorerCreateActions';
import { selectAvailableActionsForEntity } from '../../../selectors/EntityActionsSelectors';
import { SessionServerSelectors, SessionSiteSelectors, SessionUserSelectors } from '../../../selectors/SessionInfoSelectors';
import { selectSpaceInfoItem } from '../../../selectors/SpaceInfoSelectors';
import { AppState } from '../../../state/AppState';
import { SpaceType } from '../../../state/SpaceInfo';
import { hideLaunchEditOnMobile } from '../../../utils/MobileHelper';
import { CreateContentButtonLocations } from './CreateContentButton';

export type CreateContentType = ExploreContentApi_ContentType | ExplorableItemType | 'workbookUpload';
export interface ICreateContentAction {
  contentType: CreateContentType;
  isVisible(state: AppState, spaceType: SpaceType, location: CreateContentButtonLocations): boolean;
  isClickable(state: AppState, projectId?: string): boolean;
  createReduxAction(projectId?: string): ReduxAction;
  icon: string;
  title: (spaceType: SpaceType) => string | JSX.Element;
  isDivider: boolean;
}

const CREATE_PUBLISHED_CONNECTION_ACTION_SUPPORTED_ROLES = [
  SiteRoles.Creator,
  SiteRoles.ServerAdministrator,
  SiteRoles.SiteAdministratorCreator,
];

const UPLOAD_WORKBOOK_ACTION_SUPPORTED_ROLES = [
  SiteRoles.Creator,
  SiteRoles.ExplorerCanPublish,
  SiteRoles.ServerAdministrator,
  SiteRoles.SiteAdministratorCreator,
  SiteRoles.SiteAdministratorExplorer,
  SiteRoles.SupportUser,
];

const CREATE_PUBLISHED_DATASOURCE_ACTION_SUPPORTED_ROLES = [
  SiteRoles.Creator,
  SiteRoles.ServerAdministrator,
  SiteRoles.SiteAdministratorCreator
];

export const CreateFlowAction: ICreateContentAction = {
  contentType: 'flow',
  title: (spaceType: SpaceType) => {
    if (spaceType === SpaceInfoItemTypeValue.datasource) {
      return <div>{Messages.flow()}<span style={{color: CommonStyles.Colors.GreyTextColor}}>{Messages.createContentUsingThisDataSourceMessage()}</span></div>;
    }

    return Messages.flow();
  },
  icon: imgPath('spaces_small_flow.svg'),
  isVisible: (appState: AppState, spaceType: SpaceType) => selectEditingFlowsEnabled(appState) && spaceType !== 'personal',
  isClickable: (appState: AppState) => {
    const spaceItem: ISpaceTypeInfo = selectSpaceInfoItem(appState);
    if (spaceItem && spaceItem.contentType === SpaceInfoItemTypeValue.datasource) {
      const availableActionsForEntity: IDatasourceActions | undefined = selectAvailableActionsForEntity(appState, { contentType: SpaceInfoItemTypeValue.datasource, id: spaceItem.id }) as IDatasourceActions;
      return !!availableActionsForEntity && availableActionsForEntity.webEditFlow;
    }
    return SessionUserSelectors.canWebEditFlow(appState);
  },
  createReduxAction: (projectId?: string): ReduxAction => createNewFlow(projectId),
  isDivider: false
};

export const CreateLensAction: ICreateContentAction = {
  contentType: 'lens',
  title: (spaceType: SpaceType) => {
    if (spaceType === SpaceInfoItemTypeValue.datasource) {
      return <div>{Messages.lens()}<span style={{color: CommonStyles.Colors.GreyTextColor}}>{Messages.createContentUsingThisDataSourceMessage()}</span></div>;
    }
    return Messages.lens();
  },
  icon: imgPath('spaces_small_lens.svg'),
  isVisible: (_appState: AppState, spaceType: SpaceType) => {
    return FeatureFlags.nlplensContentTypeEnabled &&
      spaceType === SpaceInfoItemTypeValue.datasource &&
      SessionSiteSelectors.askDataMode(_appState) === AskDataMode.DISABLED_BY_DEFAULT;
  },
  isClickable: (appState: AppState) => { // This reuses the permission model for workbook creation
    const spaceItem: ISpaceTypeInfo = selectSpaceInfoItem(appState);
    if (spaceItem && spaceItem.contentType === SpaceInfoItemTypeValue.datasource) {
      // Do the same check here as NewWorkbookEntityAction
      const availableActionsForEntity: IDatasourceActions | undefined = selectAvailableActionsForEntity(appState, { contentType: SpaceInfoItemTypeValue.datasource, id: spaceItem.id }) as IDatasourceActions;
      return !!availableActionsForEntity &&
        availableActionsForEntity.launchWebAuthoring &&
        SiteRolesUtil.canPublish(SessionSiteSelectors.siteRole(appState));
    }
    return SessionUserSelectors.canAuthorOnWeb(appState);
  },
  createReduxAction: (projectId?: string): ReduxAction => {
    return createNewLens(projectId);
  },
  isDivider: false,
};

export const CreateProjectAction: ICreateContentAction = {
  contentType: 'project',
  title: Messages.project,
  icon: imgPath('spaces_small_projects.svg'),
  isVisible: (_appState: AppState, spaceType: SpaceType) => {
    return spaceType !== SpaceInfoItemTypeValue.datasource &&
      spaceType !== 'personal';
  },
  isClickable: (appState: AppState, projectId?: string) => {

    // If the user is site or server admin they can create a project
    // We need to check for server/site admin  to check permission for top level site
    if (SessionUserSelectors.isSessionServerAdmin(appState)) {
      return true;
    }

    if (SiteRolesUtil.isSiteAdmin(SessionSiteSelectors.siteRole(appState))) {
      return true;
    }

    if (projectId) {
      const availableActions = selectAvailableActionsForEntity(appState, {contentType: 'project', id: projectId}) as IProjectActions;
      if (availableActions && availableActions.createProject){
        return true;
      }
    }

    return false;
  },
  createReduxAction: (projectId?: string): ReduxAction => {
    return createNewProject(projectId);
  },
  isDivider: false
};

export const CreatePublishedDatasourceAction: ICreateContentAction = {
  contentType: 'datasource',
  title: Messages.createPublishedDatasource,
  icon: imgPath('datasource_18px-large.svg'),
  isVisible: (appState: AppState, spaceType: SpaceType) => {
    return FeatureFlags.editPublishedDataSourceEnabled &&
      CREATE_PUBLISHED_DATASOURCE_ACTION_SUPPORTED_ROLES.includes(SessionSiteSelectors.siteRole(appState)) &&
      spaceType !== 'personal';
  },
  isClickable: (appState: AppState, projectId?: string) => {
    return true;
  },
  createReduxAction: (projectId?: string): ReduxAction => {
    return createNewPublishedDataSource(projectId);
  },
  isDivider: false
};

export const CreatePublishedConnectionAction: ICreateContentAction = {
  contentType: 'publishedConnection',
  title: Messages.publishedConnection,
  icon: imgPath('spaces_small_published_connection.svg'),
  isVisible: (_appState: AppState, spaceType: SpaceType) => {
    return FeatureFlags.publishedConnectionEditorEnabled && spaceType !== 'personal' && spaceType !== SpaceInfoItemTypeValue.datasource;
  },
  isClickable: (appState: AppState, projectId?: string) => {
    return CREATE_PUBLISHED_CONNECTION_ACTION_SUPPORTED_ROLES.includes(SessionSiteSelectors.siteRole(appState));
  },
  createReduxAction: (projectId?: string): ReduxAction => {
    return createNewPublishedConnection(projectId);
  },
  isDivider: false
};

export const CreateWorkbookAction: ICreateContentAction = {
  contentType: 'workbook',
  title: (spaceType: SpaceType) => {
    if (spaceType === SpaceInfoItemTypeValue.datasource) {
      return <div>{Messages.workbook()}<span style={{color: CommonStyles.Colors.GreyTextColor}}>{Messages.createContentUsingThisDataSourceMessage()}</span></div>;
    } else {
      return Messages.workbook();
    }
  },
  icon: imgPath('spaces_small_workbooks.svg'),
  isVisible: (_appState: AppState) => {
    return !hideLaunchEditOnMobile();
  },
  isClickable: (appState: AppState) => {
    const spaceItem: ISpaceTypeInfo = selectSpaceInfoItem(appState);
    if (spaceItem && spaceItem.contentType === SpaceInfoItemTypeValue.datasource) {
      // Do the same check here as NewWorkbookEntityAction
      const availableActionsForEntity: IDatasourceActions | undefined = selectAvailableActionsForEntity(appState, { contentType: SpaceInfoItemTypeValue.datasource, id: spaceItem.id }) as IDatasourceActions;
      return !!availableActionsForEntity && availableActionsForEntity.launchWebAuthoring;
    }
    return SessionUserSelectors.canAuthorOnWeb(appState);
  },
  createReduxAction: (projectId?: string): ReduxAction => {
    return createNewWorkbook(projectId);
  },
  isDivider: false
};

export const CreateSiteAction = {
  title: () => Messages.newSite(),
  icon: imgPath('site_content_type_small.svg'),
  isVisible: (_appState: AppState, spaceType: SpaceType) => spaceType === 'sites',
  isClickable: (appState: AppState) => {
    return !SessionServerSelectors.siteAdminSettingsEnabled(appState);
  },
  createReduxAction: (): ReduxAction => {
    return createNewSite();
  },
  isDivider: false
};

export const CreateScheduleAction = {
  title: () => Messages.newSchedule(),
  icon: imgPath('schedule_content_type_small.svg'),
  isVisible: (appState: AppState, spaceType: SpaceType) => spaceType === 'schedules' && SessionUserSelectors.isSessionServerAdmin(appState),
  isClickable: () => true,
  createReduxAction: (type?: string, isEnabledForLinkedTasks?: boolean): ReduxAction => {
    return createNewSchedule(type, isEnabledForLinkedTasks);
  },
  isDivider: false
};

export const CreateUnnamedCollectionAction: ICreateContentAction = {
  contentType: 'collection',
  title: () => Messages.collection(),
  icon: imgPath('collection_18px-large.svg'),
  isVisible: (state: AppState, spaceType: SpaceType,location: CreateContentButtonLocations) => {
    return FeatureFlags.collectionsEnabled &&
      (spaceType === 'collections' ||
       location === CreateContentButtonLocations.Home);
  },
  isClickable: () => true,
  createReduxAction: (projectId?: string): ReduxAction => {
    return createNoNameCollectionAction();
  },
  isDivider: false
};

export const UploadWorkbookCreateMenuAction: ICreateContentAction = {
  contentType: 'workbookUpload' as any,
  title: () => Messages.contentUpload_uploadWorkbook(),
  icon: imgPath('upload_workbook_large.svg'),
  isVisible: (state: AppState, spaceType: SpaceType) => {
    return (FeatureFlags.uploadWorkbookFromWebEnabled
      && UPLOAD_WORKBOOK_ACTION_SUPPORTED_ROLES.includes(SessionSiteSelectors.siteRole(state)) &&
      spaceType !== 'personal'
    );
  },
  isClickable: () => true,
  createReduxAction: undefined,  // see handleSelect in CreateContentButton.tsx
  isDivider: false
};

export const GenericDivider: ICreateContentAction = {
  contentType: undefined,
  title: () => '',
  icon: undefined,
  isVisible: () => true,
  isClickable: () => false,
  createReduxAction: undefined,
  isDivider: true
};

export const BeforeCollectionDivider: ICreateContentAction = {
  ...GenericDivider,
  isVisible: CreateUnnamedCollectionAction.isVisible
};

export const BeforeUploadWorkbookDivider: ICreateContentAction = {
  ...GenericDivider,
  isVisible: UploadWorkbookCreateMenuAction.isVisible
};

export const OrderedCreateContentActions = [
  CreateProjectAction,
  CreateWorkbookAction,
  CreateFlowAction,
  CreatePublishedDatasourceAction,
  CreateLensAction,
  CreatePublishedConnectionAction,
  BeforeCollectionDivider,
  CreateUnnamedCollectionAction,
  BeforeUploadWorkbookDivider,
  UploadWorkbookCreateMenuAction
];
